import { api } from './client';
import type { EsitoInvito } from './inviti';

/*  Specchio dei contratti di TeraWorks.Application.Utenze.

    Nessuna password, e non per dimenticanza: l'autenticazione di TeraWorks non le usa
    ancora, e quando le userà — o quando userà Entra ID, dove dal nostro lato non
    esistono — impostarne una sarà un'operazione diversa, con un percorso suo. Qui si
    assegnano ruolo, sede e costo orario.  */

export type Ruolo = 'Operaio' | 'Manutentore' | 'Tecnico' | 'Acquisti' | 'Ufficio' | 'Amministratore';

/*  L'ordine è quello dei permessi, dal meno al più: è come si legge un menu a tendina di
    ruoli, e mettere «Amministratore» in mezzo lo renderebbe una scelta come le altre. */
export const RUOLI: { valore: Ruolo; descrizione: string }[] = [
  { valore: 'Operaio', descrizione: 'Compila i rapportini dal telefono. Non vede nessun costo.' },
  {
    valore: 'Manutentore',
    descrizione:
      'Tecnico caldaie: come l’operaio compila i rapportini, e in più emette gli scontrini dell’intervento e versa i contanti.',
  },
  /*  Riscritte il 16/10/2026: creando le utenze da Galizzi la differenza fra Tecnico e
      Ufficio non si capiva («non so quali sono le differenze»). Dicono cosa fa la
      persona, e il limite che conta di piu': chi conferma un preventivo apre una
      commessa, e quello il tecnico non lo fa.  */
  {
    valore: 'Tecnico',
    descrizione:
      'Fa i preventivi e segue le commesse; apre le richieste d’acquisto. Non conferma i preventivi e non emette ordini.',
  },
  {
    valore: 'Acquisti',
    descrizione:
      'Solo ufficio acquisti: richieste, ordini ai fornitori, bolle, subappalti, articoli e listini. Non vede preventivi, costi di commessa, rapportini né cassa.',
  },
  {
    valore: 'Ufficio',
    descrizione:
      'Ufficio acquisti: preventivi e conferme, ordini ai fornitori, carico della merce, approvazione dei rapportini.',
  },
  { valore: 'Amministratore', descrizione: 'Tutto, compresa la gestione delle utenze.' },
];

/*  `responsabilePreventivi` sta sulla persona e non sul ruolo: fra due tecnici con lo
    stesso ruolo uno lo è e l'altro no. Dice una cosa sola — questa persona può approvare
    anche un'offerta che ha scritto lei.  */
export interface NuovaUtenza {
  codice: string;
  nomeCompleto: string;
  ruolo: Ruolo;
  email?: string | null;
  codiceSede?: string | null;
  costoOrarioStandard?: number | null;
  responsabilePreventivi?: boolean;
}

export interface ModificaUtenza {
  nomeCompleto: string;
  ruolo: Ruolo;
  email?: string | null;
  codiceSede?: string | null;
  costoOrarioStandard?: number | null;
  responsabilePreventivi?: boolean;
}

export interface DettaglioUtenza {
  id: string;
  codice: string;
  nomeCompleto: string;
  email: string | null;
  ruolo: Ruolo;
  codiceSede: string | null;
  costoOrarioStandard: number | null;
  attivo: boolean;
  /** Può approvare un'offerta anche quando l'ha scritta lei. */
  responsabilePreventivi: boolean;
  /** Vero sull'utenza di chi sta guardando: serve a non offrirgli il bottone che si chiuderebbe fuori. */
  seiTu: boolean;
  /** Ha una password, quindi può entrare. Senza, la persona risulta attiva e non accede. */
  haPassword: boolean;
  permessi: string[];
}

/** Un indirizzo IP pubblico bloccato dopo troppi tentativi d'accesso falliti. */
export interface IndirizzoBloccato {
  id: string;
  indirizzo: string;
  bloccatoIl: string;
  tentativi: number;
  ultimaRotta: string | null;
}

export const utenze = {
  bloccati: () => api.get<IndirizzoBloccato[]>('/utenze/bloccati'),

  sblocca: (id: string) => api.post<void>(`/utenze/bloccati/${id}/sblocca`, {}),

  crea: (nuova: NuovaUtenza) => api.post<DettaglioUtenza>('/utenze', nuova),

  leggi: (id: string) => api.get<DettaglioUtenza>(`/utenze/${id}`),

  modifica: (id: string, modifica: ModificaUtenza) =>
    api.patch<DettaglioUtenza>(`/utenze/${id}`, modifica),

  impostaAttiva: (id: string, attiva: boolean) =>
    api.post<DettaglioUtenza>(`/utenze/${id}/attiva`, { attiva }),

  /*  Genera una password provvisoria e la restituisce in chiaro, una volta sola. Non si
      rilegge: non esiste nessuna rotta che la ridia, e se va persa se ne genera un'altra.
      Un posto da cui si possa rileggere una password è un posto da cui qualcun altro la
      rilegge.  */
  reimpostaPassword: (id: string) =>
    api.post<{ password: string }>(`/utenze/${id}/password`, {}),

  /*  La strada normale quando c'è la posta: la persona si sceglie la password da sé e
      nessuno — nemmeno chi amministra — la conosce mai. Risponde 200 anche quando non
      parte, con dentro il perché: manca l'indirizzo in anagrafica, o la posta non è
      configurata. Sono cose che chi sta guardando la scheda può sistemare.  */
  mandaInvito: (id: string) => api.post<EsitoInvito>(`/utenze/${id}/invito`, {}),
};
